"use client";

import { useRef } from "react";
import { SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";

interface Recipe {
  id: string;
  title: string;
  caption: string;
  /** Accent for the two connectors. */
  color: string;
  src: string;
  l1: string;
  slug: string;
  l2: string;
  dst: string;
  /** How this runs TODAY. None of these tools has a rii.link connector, so
   *  every recipe names its real mechanism: your own small listener on the
   *  SSE click stream (GET /v1/events/stream — clicks on API-created links,
   *  QR scans flagged is_qr) relaying to the tool's own inbound endpoint. */
  today: string;
}

const RECIPES: Recipe[] = [
  {
    id: "berlin",
    title: "QR scan in Berlin → Slack #growth",
    caption: "Know the poster works the hour it goes up.",
    color: "var(--color-data-green)",
    src: "QR scan — flyer, Berlin",
    l1: "click event · is_qr: true",
    slug: "/berlin-flyer",
    l2: "your listener → Slack",
    dst: '#growth · "🔗 Scan — /berlin-flyer, DE"',
    today: "today: link + QR via the API (POST /v1/links, /v1/qr) → your SSE listener → Slack incoming webhook",
  },
  {
    id: "launch",
    title: "Click on /launch → Zapier → sheet",
    caption: "Log every launch click where the team already looks.",
    color: "var(--color-data-violet)",
    src: "Click — launch post",
    l1: "click event",
    slug: "/launch",
    l2: "your listener → catch hook",
    dst: "Zapier — new row in a sheet",
    today: "today: link via the API (POST /v1/links) → your SSE listener → Zapier catch hook",
  },
  {
    id: "unbox",
    title: "QR scan → GA4 event",
    caption: "Offline scans next to your web analytics, without an app.",
    color: "var(--color-data-amber)",
    src: "QR scan — packaging",
    l1: "click event · is_qr: true",
    slug: "/unbox",
    l2: "your relay → GA4",
    dst: "GA4 — custom event",
    today: "today: link + QR via the API (POST /v1/links, /v1/qr) → your SSE listener → GA4 Measurement Protocol",
  },
];

function ChainBox({ children, tone }: { children: React.ReactNode; tone: "outer" | "rii" }) {
  return (
    <div
      className={`flex items-center gap-2.5 rounded-ref-sm border px-3 py-[9px] font-jetbrains text-[12px] text-ink ${
        tone === "outer"
          ? "border-line-2 bg-surface-2"
          : "border-brand-600/35 bg-[color-mix(in_srgb,var(--color-brand-600)_7%,white)]"
      }`}
    >
      {children}
    </div>
  );
}

function Connector({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2 py-[3px] pl-[17px]">
      <svg width="2" height="20" className="overflow-visible" aria-hidden="true">
        <line
          x1="1"
          y1="0"
          x2="1"
          y2="20"
          stroke={color}
          strokeWidth="1.5"
          strokeDasharray="3 5"
          className="animate-flow-dash"
        />
      </svg>
      <span className="font-jetbrains text-[10.5px] text-dim">{label}</span>
    </div>
  );
}

/**
 * Three recipes — the concrete counterpart to the catalog's one-liners: a
 * real trigger, through rii.link's click stream and a small listener of your
 * own, into the tool a team already lives in (no native connector exists).
 * Illustration, not a second signature (the ledger owns that): the only
 * motion is the connectors' crawling dashes and the once-on-enter card rise.
 */
export default function RecipeSpotlights() {
  const rootRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  // Scope kept separate from the layout-hook ref on purpose (kit convention).
  useEnterStagger(cardsRef, "[data-recipe-card]", { y: 22, duration: 0.7, stagger: 0.1 });

  return (
    <SectionShell id="recipes" className="scroll-mt-[var(--grid-cell,72px)]">
      <div ref={rootRef}>
        <SectionHead
          eyebrow="Recipes"
          title="Three flows you can wire today"
          lead="No connectors yet — a few lines of your own code on the click stream carry a real-world trigger, through rii.link, into the tool where your team already lives. The stream covers links and QR codes you create over the API."
        />

        {/* Stagger scope (cardsRef) stays OFF the [data-grid-block] the layout
            hooks write padding to — the kit's ref-split convention. */}
        <div ref={cardsRef}>
          <div data-grid-block>
            <ul className="grid list-none gap-4 md:grid-cols-3">
              {RECIPES.map((r) => (
                // GSAP tweens this element (y/opacity); the CSS hover lift and
                // its transition live one level down so the two never share a
                // property.
                <li key={r.id} data-recipe-card>
                  <div className="h-full rounded-ref-lg border border-line bg-surface p-6 shadow-ref transition-[transform,box-shadow] duration-[250ms] hover:-translate-y-0.5 hover:shadow-ref-lg">
                    <div className="text-[16px] font-bold tracking-[-0.01em] text-ink">{r.title}</div>
                    <p className="mb-5 mt-[7px] text-[13.5px] leading-[1.55] text-muted">{r.caption}</p>

                    <div className="flex flex-col">
                      <ChainBox tone="outer">{r.src}</ChainBox>
                      <Connector color={r.color} label={r.l1} />
                      <ChainBox tone="rii">
                        <span>
                          <span className="font-bold text-brand-600">rii.link</span>
                          {r.slug}
                        </span>
                      </ChainBox>
                      <Connector color={r.color} label={r.l2} />
                      <ChainBox tone="outer">{r.dst}</ChainBox>
                    </div>

                    <p className="mt-4 font-jetbrains text-[10.5px] leading-[1.5] text-dim">{r.today}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
